import { CommonModule } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { NgModule, inject } from '@angular/core';
import { RouterModule } from '@angular/router';

import { IonicModule } from '@ionic/angular';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import { Globals } from './globals';

// services
import { AuthService } from './security/auth.service';
import { OauthService } from './security/oauth/oauth.service';
import { AlarmsService } from './services/alarms/alarms.service';
import { ApplicationService } from './services/applications/applications.service';
import { CalendarService } from './services/calendar/calendar.service';
import { ConfigurationsService } from './services/configurations/configurations.service';
import { DevicesService } from './services/devices/devices.service';
import { EnergySuppliersService } from './services/energy-suppliers/energy-suppliers.service';
import { FlexibilitySettingsService } from './services/flexibility-settings/flexibility-settings.service';
import { GroupParentsService } from './services/groupParents';
import { GroupsService } from './services/groups/groups.service';
import { IconsService } from './services/icons/icons.service';
import { InstallationsService } from './services/installations/installations.service';
import { LanguagesService } from './services/languages/languages.service';
import { LocationsService } from './services/locations/locations.service';
import { NetworkdevicesService } from './services/networkdevices/networkdevices.service';
import { OrdersService } from './services/orders/orders.service';
import { QbusTranslateService } from './services/qbus-translate/qbus-translate.service';
import { SceneExtensionService } from './services/sceneExtensions/sceneExtensions.service';
import { SemsaAutoLoginService } from './services/semsa-auto-login/semsa-auto-login.service';
import { SipDevicesService } from './services/sipDevices/sip-devices.service';
import { StorageService } from './services/storage/storage.service';
import { TimezonesService } from './services/timezones/timezones.service';
import { QbusToastService } from './services/toast/qbus-toast.service';
import { TranslationsService } from './services/translations/translations.service';
import { UseractionService } from './services/useractions/useractions.service';
import { UsersService } from './services/users/users.service';
// handlers

// guards
import { AuthGuard } from './guards/auth.guard';

// directives

// interceptors

// components

// resolvers
import { CountriesResolver } from './resolvers/countries.resolver';
import { CustomersResolver } from './resolvers/customers.resolver';
import { GroupOrderTypesResolver } from './resolvers/group-order-types.resolver';
import { InstallersResolver } from './resolvers/installers.resolver';
import { LanguagesResolver } from './resolvers/languages.resolver';
import { LocationsResolver } from './resolvers/locations.resolver';
import { TimezonesResolver } from './resolvers/timezones.resolver';

import { ModuleWithProviders } from '@angular/core';
import { ConfigGlobal } from './interfaces/config-global.interfaces';
import { MessagesService } from './services/messages/messages.service';
import { MessagesCollectionService } from './services/messagescollection/messagescollection.service';
import { OutputsService } from './services/outputs/outputs.service';

import { SQLite } from '@awesome-cordova-plugins/sqlite/ngx';
import { SipHelper } from './helpers/sipHelper';

import { PushNotificationsV2Service } from './services/PushNotifications/push-notifications-v2.service';
import { AppUniqueIdService } from './services/app-unique-id/app-unique-id.service';
import { CachingService } from './services/caching/caching.service';
import { CarsService } from './services/cars';
import { CommunicationCoreService } from './services/communication/communication-core/communication-core.service';
import { CommunicationFilterService } from './services/communication/communication-filter/communication-filter.service';
import { CommunicationListenerService } from './services/communication/communication-listen/communication-listen.service';
import { CommunicationMessagesService } from './services/communication/communication-messages/communication-messages.service';
import { CommunicationSendService } from './services/communication/communication-send/communication-send.service';
import { CommunicationService } from './services/communication/communication/communication.service';
import { ErrorNotificationService } from './services/errorNotification/errorNotification.service';
import { FunctionBlocksService } from './services/functionblocks/functionblocks.service';
import { GroupOrderTypesService } from './services/group-order-types/group-order-types.service';
import { CachingInterceptorModule } from './services/http-interceptors/caching-interceptor.module';
import { HttpInterceptorModule } from './services/http-interceptors/http-interceptor.module';
import { InstallersService } from './services/installers/installers.service';
import { jwtMqttService } from './services/jwtMqtt/jwtmqtt.service';
import { QbusLocaleService } from './services/locale/qbus-locale.service';
import { LogPublishersService } from './services/log/log-publishers.service';
import { LogService } from './services/log/log.service';
import { OfflineCommunicationService } from './services/offlineCommunication/offlinecommunication.service';
import { ProIconGroupsService } from './services/pro-icon-groups/pro-icon-groups.service';
import { QrCodeLoginService } from './services/qr-code-login/qr-code-login.service';
import { AppInitSettingsService } from './services/settings/app-init-settings.service';
import { SettingsService } from './services/settings/settings.service';
import { SharedAccountsService } from './services/sharedAccounts/shared-accounts.service';
import { TransportConfigLoaderService } from './services/transport-config/transport-config-loader.service';

@NgModule({
  declarations: [],
  exports: [TranslateModule],
  imports: [
    RouterModule,
    CommonModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: TranslationsService,
        deps: [HttpClient, Globals],
      },
    }),
    IonicModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CachingInterceptorModule,
      multi: true,
      deps: [CachingService, Globals, OfflineCommunicationService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorModule,
      multi: true,
    },
    AppUniqueIdService,
    Globals,
    AuthService,
    AuthGuard,
    ConfigurationsService,
    DevicesService,
    CommunicationListenerService,
    CommunicationSendService,
    CommunicationCoreService,
    CommunicationFilterService,
    CommunicationService,
    CommunicationMessagesService,
    SipDevicesService,
    InstallationsService,
    LanguagesService,
    NetworkdevicesService,
    TimezonesService,
    UsersService,
    SemsaAutoLoginService,
    TranslationsService,
    PushNotificationsV2Service,
    OrdersService,
    LanguagesResolver,
    TimezonesResolver,
    InstallersResolver,
    LocationsResolver,
    GroupOrderTypesResolver,
    CountriesResolver,
    LocationsService,
    GroupOrderTypesService,
    IconsService,
    CachingService,
    OutputsService,
    FunctionBlocksService,
    SharedAccountsService,
    MessagesService,
    MessagesCollectionService,
    GroupsService,
    InstallersService,
    CustomersResolver,
    SettingsService,
    AppInitSettingsService,
    OauthService,
    LogService,
    LogPublishersService,
    ErrorNotificationService,
    UseractionService,
    AlarmsService,
    ApplicationService,
    CalendarService,
    SceneExtensionService,
    StorageService,
    SQLite,
    QbusToastService,
    QbusLocaleService,
    OfflineCommunicationService,
    jwtMqttService,
    GroupParentsService,
    QbusTranslateService,
    QbusTranslateService,
    CarsService,
    FlexibilitySettingsService,
    EnergySuppliersService,
    QrCodeLoginService,
    ProIconGroupsService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class CoreModule {
  constructor() {
    const parentModule = inject(CoreModule, { optional: true, skipSelf: true });

    if (parentModule) {
      throw new Error(
        'CoreModule is already loaded. Import it in the AppModule only',
      );
    }
  }

  static forRoot(config: ConfigGlobal): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [
        Globals,
        { provide: 'config', useValue: config },
        { provide: 'http', useClass: HttpClient },
        { provide: 'globals', useClass: Globals },
        SettingsService,
        AppInitSettingsService,
        AuthService,
        AuthGuard,
        ConfigurationsService,
        DevicesService,
        SipDevicesService,
        InstallationsService,
        OrdersService,
        LanguagesService,
        NetworkdevicesService,
        TimezonesService,
        UsersService,
        SemsaAutoLoginService,
        TranslationsService,
        MessagesService,
        MessagesCollectionService,
        TransportConfigLoaderService,
        CommunicationCoreService,
        CommunicationFilterService,
        CommunicationService,
        CommunicationListenerService,
        CommunicationSendService,
        InstallersService,
        OauthService,
        ErrorNotificationService,
        LogService,
        LogPublishersService,
        CalendarService,
        SceneExtensionService,
        StorageService,
        QbusToastService,
        QbusLocaleService,
        CachingService,
        OfflineCommunicationService,
        jwtMqttService,
        SipHelper,
        FlexibilitySettingsService,
        EnergySuppliersService,
        QrCodeLoginService,
      ],
    };
  }
}
